import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Radio,
  Button,
  TextField,
  IconButton,
  Chip,
} from "@mui/material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { errorTost, successTost } from '../../Services/Toast'
const steps = [
  "Plan Details",
  "POP Validity",
  "Scope Type",
  "Scope Selection",
  "Calculation Rules",
  "Eligibility",
  "Conflict Settings",
  "CP Sign-Off",
  "Review",
];

const OptionBox = ({ selected, title, subtitle, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: "pointer",
      border: "1px solid",
      borderColor: selected ? "#4F6BED" : "divider",
      backgroundColor: selected ? "#EFF6FF" : "#fff",
      borderRadius: 2,
      p: 2,
      display: "flex",
      gap: 2,
      transition: "0.2s",
      "&:hover": {
        borderColor: "#4F6BED",
      },
    }}
  >
    <Radio checked={selected} />
    <Box>
      <Typography fontWeight={600}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const CreatePopPlanStep5Plans = ({ data, setData, onBack, onNext }) => {
  const updateRule = (index, field, value) => {
    const updated = [...data.rangeRules];
    updated[index] = { ...updated[index], [field]: value };
    setData({ ...data, rangeRules: updated });
  };

  const addRule = () => {
    setData({
      ...data,
      rangeRules: [...data.rangeRules, { from: "", to: "", amount: "" }],
    });
  };

  const removeRule = (index) => {
    if (data.rangeRules.length === 1) return;

    setData({
      ...data,
      rangeRules: data.rangeRules.filter((_, i) => i !== index),
    });
  };

const handleNextClick = () => {
  for (let i = 0; i < data.rangeRules.length; i++) {
    const rule = data.rangeRules[i];

    if (!rule.from || !rule.to || !rule.amount) {
      errorTost("Please fill all slab fields");
      return;
    }

    if (Number(rule.from) > Number(rule.to)) {
      errorTost("From Value cannot be greater than To Value");
      return;
    }

    // Check for overlap with the next rule
    if (i < data.rangeRules.length - 1) {
      const nextRule = data.rangeRules[i + 1];
      if (Number(rule.to) >= Number(nextRule.from)) {
        errorTost(`Slab ${i + 1} overlaps with Slab ${i + 2}`);
        return;
      }
    }
  }

  onNext();
};
  const handleBasisChange = (type) => {
    setData({
      ...data,
      calculationBasis: type,
      rangeRules: [{ from: "", to: "", amount: "" }],
    });
  };

  const handleRewardTypeChange = (type) => {
    setData({
      ...data,
      rewardType: type,
      rangeRules: [{ from: "", to: "", amount: "" }],
    });
  };
  const formatIndianNumber = (value) => {
  if (!value) return "";
  return Number(value).toLocaleString("en-IN");
};

const getNumericValue = (value) => {
  return value.replace(/[^0-9]/g, "");
};

  return (
    <Box p={1}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{ mb: 2, cursor: "pointer", color: "text.secondary" }}
        onClick={onBack}
      >
        ← Back to POPs
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={4} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  width: 36,
                  height: 36,
                  "&.Mui-completed": { color: "#2563EB" },
                  "&.Mui-active": {
                    color: "#2563EB",
                    transform: "scale(1.15)",
                  },
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Typography variant="subtitle2" color="text.secondary" mb={2}>
        Step 5 of 9: <strong>Calculation Rules</strong> — Define slabs
      </Typography>

      {/* RULE TYPE SECTION */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          mb: 3,
          backgroundColor: "#fff",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <LayersOutlinedIcon />
          <Typography fontWeight={600}>Rule Type</Typography>
        </Box>

        <Typography variant="subtitle2" mb={1}>
          Calculation Basis
        </Typography>

        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <OptionBox
            selected={data.calculationBasis === "value"}
            onClick={() => handleBasisChange("value")}
            title="Agreement Value Based"
            subtitle="Calculate based on total agreement value"
          />

          <OptionBox
            selected={data.calculationBasis === "count"}
            onClick={() => handleBasisChange("count")}
            title="Number of Agreements"
            subtitle="Calculate based on agreement count"
          />
        </Box>

        <Typography variant="subtitle2" mt={3} mb={1}>
          Reward Type
        </Typography>

        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <OptionBox
            selected={data.rewardType === "percentage"}
            onClick={() => handleRewardTypeChange("percentage")}
            title="Percentage on Value"
            subtitle="Percentage of agreement value"
          />

          <OptionBox
            selected={data.rewardType === "fixed"}
            onClick={() => handleRewardTypeChange("fixed")}
            title="Fixed Amount"
            subtitle="Fixed amount per slab"
          />
        </Box>
      </Box>

      {/* RANGE RULES SECTION */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fff",
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontWeight={600}>Range Rules</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
            Add Rule
          </Button>
        </Box>

     {data.rangeRules.map((rule, index) => (
  <Box
    key={index}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: 2,
      mb: 2,
    }}
  >
    <Box
      display="grid"
      gridTemplateColumns="40px 1fr 1fr 1fr 40px"
      gap={2}
      alignItems="center"
    >
      <Chip label={index + 1} size="small" />

   <TextField
  label={
    data.calculationBasis === "value"
      ? "From Value"
      : "From Agreements"
  }
  size="small"
  value={formatIndianNumber(rule.from)}
  onChange={(e) =>
    updateRule(index, "from", getNumericValue(e.target.value))
  }
  inputProps={{ inputMode: "numeric" }}
  error={isNaN(rule.from)}
  helperText={
    isNaN(rule.from) && (
      <span style={{ fontSize: "14px", color: "#f44336" }}>
        Please enter a valid number
      </span>
    )
  }
/>
<TextField
  label={
    data.calculationBasis === "value"
      ? "To Value"
      : "To Agreements"
  }
  size="small"
  value={formatIndianNumber(rule.to)}
  onChange={(e) =>
    updateRule(index, "to", getNumericValue(e.target.value))
  }
  inputProps={{ inputMode: "numeric" }}
  error={isNaN(rule.to)}
  helperText={
    isNaN(rule.to) && (
      <span style={{ fontSize: "14px", color: "#f44336" }}>
        Please enter a valid number
      </span>
    )
  }
/>

    <TextField
  label={
    data.rewardType === "percentage"
      ? "Brokerage %"
      : "Amount (₹)"
  }
  size="small"
  value={
    data.rewardType === "percentage"
      ? rule.amount
      : formatIndianNumber(rule.amount)
  }
  onChange={(e) =>
    updateRule(index, "amount", getNumericValue(e.target.value))
  }
  inputProps={{ inputMode: "numeric" }}
  error={isNaN(rule.amount)}
  helperText={
    isNaN(rule.amount) && (
      <span style={{ fontSize: "14px", color: "#f44336" }}>
        Please enter a valid number
      </span>
    )
  }
/>

      <IconButton onClick={() => removeRule(index)}>
        <DeleteOutlineIcon />
      </IconButton>
    </Box>
  </Box>
))}
      </Box>

      {/* FOOTER */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button variant="outlined" onClick={onBack}>
          ← Back
        </Button>
        <Button variant="contained" onClick={handleNextClick}>
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePopPlanStep5Plans;